import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { KeyValue, StatusPill } from "@ozpanel/kit";
import { Panel, Section, StateView, useAuth } from "@ozpanel/ui";

/**
 * PostgreSQL synchronous replication, and whether the pooler follows the primary.
 *
 * /repmgrd/sync is the repmgrd plane's LAST MEASUREMENT on the node serving this page (taken every
 * gate pass) — it is not a cluster-wide answer. It is labelled "this node" for that reason: on a
 * standby it answers for that standby. The cluster-wide view (ClusterSyncReplicationPanel, below)
 * reads /cluster/sync-replication, which asks every node.
 *
 * The two facts belong together because a commit acknowledged under synchronous_commit=on is safe
 * only on the recorded synchronous standby, and it is only reachable at all if the pooler points at
 * the primary.
 */

interface SyncState {
  managed: boolean;
  measured: boolean;
  am_primary: boolean;
  standby_names_required: string;
  standby_names_running: string;
  synchronous_commit: string;
  sync_standby: string;
  first_streaming_successor: string;
  released: boolean;
  recorded_sync_standby: string;
  recorded: boolean;
  in_force: boolean;
  note: string;
}

interface PoolerFailover {
  primary?: string;
  primary_error?: string;
  pooler_backend?: string;
  following?: boolean;
  backend_writable?: boolean;
  checked_via?: string;
  backend_error?: string;
  events?: string;
  hook_effective?: boolean;
  repmgr_config?: string;
  repmgrd?: string;
  warning?: string;
  note?: string;
}

const notReported = <span className="text-muted">not reported</span>;

function text(v: string | undefined, empty: React.ReactNode = <span className="text-muted">none</span>) {
  if (v === undefined) return notReported;
  return v === "" ? empty : <span className="font-mono">{v}</span>;
}

function Warn({ tone, children }: { tone: "crit" | "warn" | "ok"; children: React.ReactNode }) {
  const c = tone === "crit" ? "var(--ozp-crit)" : tone === "ok" ? "var(--ozp-ok)" : "var(--ozp-warn)";
  return (
    <div
      className="rounded border px-3 py-2 text-[12.5px] leading-relaxed"
      style={{ borderColor: `color-mix(in oklab, ${c} 38%, transparent)` }}
    >
      {children}
    </div>
  );
}

export function SyncReplicationPanel() {
  const { client } = useAuth();
  const sync = useQuery({
    queryKey: ["repmgrd-sync"],
    queryFn: () => client.apiFetch<SyncState>("/repmgrd/sync"),
    refetchInterval: 15000,
    retry: false,
  });
  const pool = useQuery({
    queryKey: ["pgbouncer-failover"],
    queryFn: () => client.apiFetch<PoolerFailover>("/pgbouncer/failover"),
    refetchInterval: 15000,
    retry: false,
  });
  const s = sync.data;
  const p = pool.data;

  // The badge: in force only when measured AND the running list equals the required one AND the
  // server-wide default is local — all decided by the server. Unmeasured is never "in force".
  const syncBadge = !s ? null : !s.managed ? (
    <StatusPill tone="neutral">not managed</StatusPill>
  ) : !s.measured ? (
    <StatusPill tone="warn">not measured yet</StatusPill>
  ) : s.released ? (
    <StatusPill tone="bad">released</StatusPill>
  ) : s.in_force ? (
    <StatusPill tone="ok">in force</StatusPill>
  ) : (
    <StatusPill tone="bad">NOT in force</StatusPill>
  );

  return (
    <Section
      title="PostgreSQL synchronous replication"
      hint="measured on THIS node — the node serving this page"
      actions={syncBadge}
      bodyClassName="p-0"
    >
      <div className="grid gap-4 p-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted">
            Synchronous replication · this node{s ? (s.am_primary ? " (primary)" : " (not primary)") : ""}
          </div>
          <StateView loading={sync.isLoading} error={sync.error} errorContext="synchronous replication (/repmgrd/sync)">
            {s && (
              <>
                {s.released && (
                  <Warn tone="crit">
                    <b className="text-crit">Synchronous replication is RELEASED.</b> This primary is a lone
                    survivor: no node in the succession is streaming, so it serves writes without a
                    synchronous standby — writes are not copied to another node before they are
                    acknowledged. A failure of this node now loses acknowledged commits.
                  </Warn>
                )}
                {s.managed && s.measured && !s.in_force && !s.released && (
                  <Warn tone="crit">
                    <b className="text-crit">Not in force.</b> What PostgreSQL runs differs from what the signed
                    succession requires, or synchronous_commit is not the expected server default.
                  </Warn>
                )}
                {!s.managed && (
                  <Warn tone="warn">This node's repmgrd plane does not manage synchronous replication.</Warn>
                )}
                <Panel className="overflow-hidden">
                  <KeyValue
                    dense
                    rows={[
                      { key: "measured", label: "Measured", value: s.measured },
                      {
                        key: "required",
                        label: "Standby names required",
                        value: s.standby_names_required,
                        render: () => text(s.standby_names_required, <span className="text-muted">empty</span>),
                        hint: "from the signed succession order",
                      },
                      {
                        key: "running",
                        label: "Standby names running",
                        value: s.standby_names_running,
                        render: () => (
                          <span className="flex items-center justify-end gap-2">
                            {text(s.standby_names_running, <span className="text-muted">empty</span>)}
                            {s.measured && (
                              <StatusPill tone={s.standby_names_running === s.standby_names_required ? "ok" : "bad"}>
                                {s.standby_names_running === s.standby_names_required ? "matches" : "differs"}
                              </StatusPill>
                            )}
                          </span>
                        ),
                        hint: "what PostgreSQL reports (synchronous_standby_names)",
                      },
                      { key: "commit", label: "synchronous_commit", value: s.synchronous_commit, render: () => text(s.synchronous_commit, notReported) },
                      {
                        key: "sync_standby",
                        label: "Sync standby (now)",
                        value: s.sync_standby,
                        render: () => text(s.sync_standby),
                        hint: s.am_primary ? undefined : "only a primary has a synchronous standby",
                      },
                      { key: "first", label: "First streaming successor", value: s.first_streaming_successor, render: () => text(s.first_streaming_successor) },
                      {
                        key: "recorded",
                        label: "Recorded sync standby",
                        value: s.recorded_sync_standby,
                        render: () =>
                          s.recorded ? (
                            text(s.recorded_sync_standby)
                          ) : (
                            <StatusPill tone="warn">never recorded</StatusPill>
                          ),
                        hint: "the only node that may be promoted — it alone holds every commit acknowledged under synchronous_commit=on",
                      },
                      {
                        key: "released",
                        label: "Released",
                        value: s.released,
                        render: () => <StatusPill tone={s.released ? "bad" : "ok"}>{s.released ? "yes — lone survivor" : "no"}</StatusPill>,
                      },
                    ]}
                  />
                </Panel>
                {s.note && <div className="text-[12px] leading-relaxed text-muted">{s.note}</div>}
              </>
            )}
          </StateView>
        </div>

        <div className="space-y-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted">Pooler follows primary · this node</div>
          <StateView loading={pool.isLoading} error={pool.error} errorContext="the pooler's failover state (/pgbouncer/failover)">
            {p && (
              <>
                {p.warning && (
                  <Warn tone="crit">
                    <span className="text-crit">{p.warning}</span>
                  </Warn>
                )}
                {p.primary_error && (
                  <Warn tone="crit">
                    <b>The primary could not be determined:</b> <span className="font-mono break-all">{p.primary_error}</span>
                  </Warn>
                )}
                {p.backend_error && (
                  <Warn tone="crit">
                    <b>Backend not proven writable:</b> <span className="font-mono break-all">{p.backend_error}</span>
                  </Warn>
                )}
                <Panel className="overflow-hidden">
                  <KeyValue
                    dense
                    rows={[
                      {
                        key: "following",
                        label: "Pooler follows primary",
                        value: p.following,
                        render: () =>
                          p.following === undefined ? notReported : (
                            <StatusPill tone={p.following ? "ok" : "bad"}>{p.following ? "following" : "NOT following"}</StatusPill>
                          ),
                      },
                      { key: "primary", label: "Primary", value: p.primary, render: () => text(p.primary, <span className="text-muted">unknown</span>) },
                      { key: "backend", label: "Pooler backend", value: p.pooler_backend, render: () => text(p.pooler_backend, <span className="text-muted">none in the pooler's config</span>) },
                      {
                        key: "writable",
                        label: "Backend writable",
                        value: p.backend_writable,
                        render: () =>
                          p.backend_writable === undefined ? notReported : (
                            <StatusPill tone={p.backend_writable ? "ok" : "bad"}>{p.backend_writable ? "writable" : "not writable"}</StatusPill>
                          ),
                        hint: p.checked_via ? <>checked via {p.checked_via}</> : undefined,
                      },
                      {
                        key: "hook",
                        label: "repmgr hook effective",
                        value: p.hook_effective,
                        render: () =>
                          p.hook_effective === undefined ? notReported : (
                            <StatusPill tone={p.hook_effective ? "ok" : "bad"}>{p.hook_effective ? "yes" : "no"}</StatusPill>
                          ),
                        hint: p.events ? <>events: <span className="font-mono">{p.events}</span></> : undefined,
                      },
                      { key: "repmgr_config", label: "repmgr config", value: p.repmgr_config, render: () => text(p.repmgr_config, notReported) },
                      {
                        key: "repmgrd",
                        label: "repmgrd",
                        value: p.repmgrd,
                        render: () =>
                          p.repmgrd === undefined ? notReported : (
                            <StatusPill tone={p.repmgrd === "active" ? "ok" : "bad"}>{p.repmgrd || "unknown"}</StatusPill>
                          ),
                      },
                    ]}
                  />
                </Panel>
                {p.note && <div className="text-[12px] leading-relaxed text-muted">{p.note}</div>}
              </>
            )}
          </StateView>
        </div>
      </div>
      <div className="border-t border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">
        Both answers are for this node only. Every node's view, with a cluster verdict, is on the
        Cluster screen (Nodes tab).
      </div>
    </Section>
  );
}

// ── Cluster-wide: every node's own answer, from /cluster/sync-replication ──────────────────

/** The juicefs plane's two-writer fence on one node (juicefs FenceState). */
interface JuiceFence {
  measured?: boolean;
  writers?: string[] | null;
  unmeasured?: string[] | null;
  rogue_writer_here?: boolean;
  off_declared_writer?: boolean;
  off_passes?: number;
  fenced?: boolean;
  fence_reason?: string;
  clean_passes?: number;
}
interface NodeSync {
  node: string;
  measured: boolean;
  error?: string;
  /** pooler and juicefs are absent on a node where that module is not built. */
  state?: { node?: string; sync?: SyncState; pooler?: PoolerFailover; juicefs?: JuiceFence };
}
interface ClusterSync {
  ok: boolean;
  verdict: string;
  problems: string[] | null;
  at: string;
  note: string;
  nodes: NodeSync[] | null;
}

function Row({ label, children, hint }: { label: string; children: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div className="px-3 py-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="shrink-0 text-[12px] text-muted">{label}</span>
        <span className="min-w-0 break-all text-right text-[12.5px]">{children}</span>
      </div>
      {hint && <div className="mt-0.5 text-[11.5px] leading-relaxed text-muted">{hint}</div>}
    </div>
  );
}

function NodeSyncCard({ n }: { n: NodeSync }) {
  const crit = "border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)]";
  if (!n.measured) {
    // An unmeasured node is UNKNOWN — rendered as such, never as a healthy-looking card.
    return (
      <Panel className={`overflow-hidden ${crit}`}>
        <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
          <span className="font-mono text-[13px]">{n.node}</span>
          <StatusPill tone="bad">UNMEASURED</StatusPill>
        </div>
        <div className="px-3 py-2 text-[12px] leading-relaxed">
          <span className="text-crit">Could not be asked — its state is unknown, not in force.</span>
          <div className="mt-1 font-mono text-[11.5px] break-all text-muted">{n.error || "no error text reported"}</div>
        </div>
      </Panel>
    );
  }
  const s = n.state?.sync;
  const p = n.state?.pooler;
  const j = n.state?.juicefs;
  const bad = !s || s.released || !s.in_force || p?.following === false || j?.rogue_writer_here === true;
  return (
    <Panel className={`overflow-hidden ${bad ? crit : ""}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
        <span className="font-mono text-[13px]">{n.node}</span>
        <span className="flex flex-wrap gap-1.5">
          {s ? <StatusPill tone={s.am_primary ? "accent" : "neutral"}>{s.am_primary ? "primary" : "standby"}</StatusPill> : null}
          {!s ? (
            <StatusPill tone="bad">no sync state reported</StatusPill>
          ) : !s.managed ? (
            <StatusPill tone="neutral">not managed</StatusPill>
          ) : !s.measured ? (
            <StatusPill tone="warn">not measured yet</StatusPill>
          ) : s.released ? (
            <StatusPill tone="bad">released</StatusPill>
          ) : (
            <StatusPill tone={s.in_force ? "ok" : "bad"}>{s.in_force ? "in force" : "NOT in force"}</StatusPill>
          )}
        </span>
      </div>
      <div className="divide-y divide-line">
        {s && s.released && (
          <div className="px-3 py-2 text-[12px] leading-relaxed text-crit">
            <b>RELEASED — lone survivor.</b> Writes are not copied to another node before they are acknowledged.
          </div>
        )}
        {j?.rogue_writer_here && (
          <div className="px-3 py-2 text-[12px] leading-relaxed text-crit">
            <b>Rogue writer here:</b> this node's database is a primary that is not the declared writer.
          </div>
        )}
        {s && (
          <>
            <Row label="Standby names required">{text(s.standby_names_required, <span className="text-muted">empty</span>)}</Row>
            <Row label="Standby names running">
              <span className="flex items-center justify-end gap-2">
                {text(s.standby_names_running, <span className="text-muted">empty</span>)}
                {s.measured && (
                  <StatusPill tone={s.standby_names_running === s.standby_names_required ? "ok" : "bad"}>
                    {s.standby_names_running === s.standby_names_required ? "matches" : "differs"}
                  </StatusPill>
                )}
              </span>
            </Row>
            <Row label="synchronous_commit">{text(s.synchronous_commit, notReported)}</Row>
            {s.am_primary && (
              <>
                <Row label="Sync standby (now)">{text(s.sync_standby, <StatusPill tone="bad">none</StatusPill>)}</Row>
                <Row label="First streaming successor">{text(s.first_streaming_successor)}</Row>
              </>
            )}
            <Row label="Recorded sync standby" hint="the only node that may be promoted">
              {s.recorded ? text(s.recorded_sync_standby) : <StatusPill tone="warn">never recorded</StatusPill>}
            </Row>
          </>
        )}
        {p ? (
          <>
            <Row label="Pooler backend">{text(p.pooler_backend, <span className="text-muted">none in the pooler's config</span>)}</Row>
            <Row label="Pooler follows primary">
              {p.following === undefined ? notReported : (
                <StatusPill tone={p.following ? "ok" : "bad"}>{p.following ? "following" : "NOT following"}</StatusPill>
              )}
            </Row>
            {p.warning && <div className="px-3 py-1.5 text-[11.5px] leading-relaxed text-crit">{p.warning}</div>}
          </>
        ) : (
          <Row label="Pooler">{notReported}</Row>
        )}
        {j ? (
          <Row
            label="JuiceFS fence"
            hint={
              j.fenced && j.fence_reason ? <span className="font-mono">{j.fence_reason}</span> : undefined
            }
          >
            {j.measured === false ? (
              <StatusPill tone="warn">not measured yet</StatusPill>
            ) : j.fenced === undefined ? (
              notReported
            ) : (
              <StatusPill tone={j.fenced ? "bad" : "ok"}>{j.fenced ? "FENCED" : "not fenced"}</StatusPill>
            )}
          </Row>
        ) : (
          <Row label="JuiceFS fence">{notReported}</Row>
        )}
      </div>
    </Panel>
  );
}

/**
 * The Cluster screen's view: /cluster/sync-replication asks EVERY node for its own measurement
 * (repmgrd sync state, pooler, juicefs fence) and reduces them to a verdict. A node that could not be
 * asked is UNMEASURED and is rendered as unknown, never as healthy.
 */
export function ClusterSyncReplicationPanel() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-sync-replication"],
    queryFn: () => client.apiFetch<ClusterSync>("/cluster/sync-replication"),
    refetchInterval: 15000,
    retry: false,
  });
  const d = q.data;
  const nodes = d?.nodes ?? [];
  const problems = d?.problems ?? [];

  return (
    <Section
      title="PostgreSQL synchronous replication"
      hint="every node's own measurement, asked now"
      actions={d ? <StatusPill tone={d.ok ? "ok" : "bad"}>{d.ok ? "one state" : `${problems.length} problem(s)`}</StatusPill> : undefined}
      bodyClassName="p-0"
    >
      <div className="space-y-3 p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="cluster-wide synchronous replication (/cluster/sync-replication)">
          {d && (
            <>
              {d.ok ? (
                <Warn tone="ok">
                  <span className="text-ok">
                    <b>OK:</b> {d.verdict}
                  </span>
                </Warn>
              ) : (
                <Warn tone="crit">
                  <div className="text-crit">
                    <b>{d.verdict}</b>
                  </div>
                  {problems.length > 0 && (
                    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-crit">
                      {problems.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  )}
                </Warn>
              )}
              {nodes.length === 0 ? (
                <Warn tone="crit">
                  <span className="text-crit">The route answered with no nodes at all — nothing was measured.</span>
                </Warn>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {nodes.map((n) => (
                    <NodeSyncCard key={n.node} n={n} />
                  ))}
                </div>
              )}
              <div className="text-[12px] leading-relaxed text-muted">
                {d.note}
                {d.at ? <> · measured {new Date(d.at).toLocaleTimeString()}</> : null}
              </div>
            </>
          )}
        </StateView>
      </div>
    </Section>
  );
}
